/**
 * Suburb search — top-of-map dropdown that flies to the selected area
 * and opens its verdict popup.
 *
 * Design contract (rewritten):
 *   - No `<datalist>`. Browsers show the datalist list on focus with the
 *     entire dataset (60+ suburbs), which felt "disoriented" and covered
 *     the map on small viewports. We use a custom absolutely-positioned
 *     panel anchored directly below the input instead.
 *   - The panel is hidden until the user types at least ONE character.
 *     Empty input → no panel, full map visible.
 *   - Results are prefix-first then substring (case-insensitive). Exact
 *     matches bubble to the top. Capped at 8 rows so the dropdown stays
 *     short on phone viewports.
 *   - Keyboard: ↑/↓ navigate the list, Enter selects, Escape clears/closes.
 *   - Mouse/touch: tap a row to select.
 *   - Install-time: coords/names are baked in once. Dataset is stable
 *     within a session (city switch forces a page reload).
 */

import type maplibregl from "maplibre-gl";
import type { Verdict } from "../data/verdicts";
import { showVerdict } from "./verdict-card";

const WRAP_ID = "hyperlocal-suburb-search";
const LIST_ID = "hyperlocal-suburb-search-list";
const MAX_RESULTS = 8;

export function installSuburbSearch(
  map: maplibregl.Map,
  verdicts: readonly Verdict[]
): void {
  if (document.getElementById(WRAP_ID)) return;

  // Alphabetical baseline — ranking below prefers prefix matches, but
  // amongst ties we keep the predictable A→Z order.
  const sorted = [...verdicts].sort((a, b) => a.name.localeCompare(b.name));
  const byNameLower = new Map<string, Verdict>();
  for (const v of sorted) byNameLower.set(v.name.toLowerCase(), v);

  const wrap = document.createElement("div");
  wrap.id = WRAP_ID;
  // Top-center so the left rail and right decision-lens don't collide.
  // Width adapts to viewport but caps at a readable size. `position:fixed`
  // so scrolling never drifts it off-axis from the input we anchor below.
  wrap.style.cssText = `
    position:fixed;top:16px;left:50%;transform:translateX(-50%);
    z-index:1250;width:min(320px, calc(100vw - 380px));
    pointer-events:auto;
  `;

  const input = document.createElement("input");
  input.type = "search";
  input.setAttribute("role", "combobox");
  input.setAttribute("aria-autocomplete", "list");
  input.setAttribute("aria-expanded", "false");
  input.setAttribute("aria-controls", LIST_ID);
  input.setAttribute("placeholder", "Search a suburb\u2026");
  input.setAttribute("aria-label", "Search a suburb");
  input.setAttribute("autocomplete", "off");
  input.setAttribute("spellcheck", "false");
  input.style.cssText = `
    width:100%;box-sizing:border-box;
    padding:9px 14px;
    background:rgba(22,22,22,0.94);backdrop-filter:blur(12px);
    color:#f1f1f1;
    border:1px solid rgba(255,214,128,0.22);
    border-radius:999px;
    font:600 13px/1.3 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;
    letter-spacing:0.01em;
    outline:none;
    box-shadow:0 6px 18px rgba(0,0,0,0.4);
    transition:border-color 140ms ease,box-shadow 140ms ease;
  `;

  // Custom dropdown panel — positioned flush under the input. Absolute
  // positioning inside `wrap` means width matches the input automatically
  // and it never drifts away from it on resize.
  const listbox = document.createElement("ul");
  listbox.id = LIST_ID;
  listbox.setAttribute("role", "listbox");
  listbox.style.cssText = `
    position:absolute;top:calc(100% + 6px);left:0;right:0;
    margin:0;padding:4px;
    list-style:none;
    background:rgba(22,22,22,0.96);backdrop-filter:blur(12px);
    border:1px solid rgba(255,214,128,0.22);
    border-radius:14px;
    box-shadow:0 12px 28px rgba(0,0,0,0.55);
    max-height:min(42vh, 320px);
    max-height:min(42dvh, 320px);
    overflow-y:auto;
    overscroll-behavior:contain;
    -webkit-overflow-scrolling:touch;
    display:none;
  `;

  input.addEventListener("focus", () => {
    input.style.borderColor = "rgba(255,214,128,0.55)";
    input.style.boxShadow =
      "0 6px 18px rgba(0,0,0,0.4), 0 0 0 2px rgba(255,214,128,0.18)";
    // IMPORTANT: do NOT show the dropdown on focus. The panel only
    // appears after the user types at least one character. This is the
    // whole point of the rewrite — focus alone showing 60+ rows was
    // the "disoriented" problem.
    if (input.value.trim().length >= 1) renderList(input.value);
  });
  input.addEventListener("blur", () => {
    input.style.borderColor = "rgba(255,214,128,0.22)";
    input.style.boxShadow = "0 6px 18px rgba(0,0,0,0.4)";
    // Defer hide so a mousedown on a row still registers as a click. The
    // row handler calls hideList() once the selection is committed.
    setTimeout(() => hideList(), 120);
  });

  // ------------- list render + filter --------------------------------

  let activeIndex = -1; // keyboard-highlight index within the CURRENT list

  function rank(q: string): Verdict[] {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    const prefix: Verdict[] = [];
    const contains: Verdict[] = [];
    for (const v of sorted) {
      const n = v.name.toLowerCase();
      if (n.startsWith(needle)) prefix.push(v);
      else if (n.includes(needle)) contains.push(v);
    }
    return [...prefix, ...contains].slice(0, MAX_RESULTS);
  }

  function renderList(q: string): void {
    const matches = rank(q);
    listbox.textContent = "";
    if (matches.length === 0) {
      // Empty-result message — small, muted, still inside the panel so
      // the user gets feedback instead of a dead dropdown.
      const empty = document.createElement("li");
      empty.setAttribute("role", "presentation");
      empty.style.cssText = `
        padding:10px 12px;color:#888;
        font:500 12px/1.3 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;
        text-align:center;letter-spacing:0.02em;
      `;
      empty.textContent = "No matches";
      listbox.appendChild(empty);
    } else {
      matches.forEach((v, i) => {
        const row = document.createElement("li");
        row.setAttribute("role", "option");
        row.setAttribute("id", `${LIST_ID}-opt-${i}`);
        row.setAttribute("aria-selected", "false");
        row.dataset.name = v.name;
        row.style.cssText = `
          padding:9px 12px;
          border-radius:10px;
          color:#f1f1f1;
          font:500 13px/1.3 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;
          cursor:pointer;
          transition:background 120ms ease;
          user-select:none;
        `;
        row.textContent = v.name;
        // `mousedown` (not `click`) so the selection commits BEFORE the
        // input's `blur` fires — `click` after blur would race the
        // hideList() timeout.
        row.addEventListener("mousedown", (e) => {
          e.preventDefault(); // keep input focused; selection clears value next
          selectByName(v.name);
        });
        row.addEventListener("mouseenter", () => setActiveIndex(i));
        listbox.appendChild(row);
      });
    }
    activeIndex = matches.length > 0 ? 0 : -1;
    paintActive();
    listbox.style.display = "block";
    input.setAttribute("aria-expanded", "true");
  }

  function hideList(): void {
    listbox.style.display = "none";
    activeIndex = -1;
    input.setAttribute("aria-expanded", "false");
  }

  function paintActive(): void {
    const rows = Array.from(listbox.querySelectorAll('li[role="option"]')) as HTMLLIElement[];
    rows.forEach((r, i) => {
      const on = i === activeIndex;
      r.style.background = on ? "rgba(255,214,128,0.16)" : "transparent";
      r.setAttribute("aria-selected", on ? "true" : "false");
      if (on) input.setAttribute("aria-activedescendant", r.id);
    });
    if (activeIndex < 0) input.removeAttribute("aria-activedescendant");
  }

  function setActiveIndex(i: number): void {
    const rows = listbox.querySelectorAll('li[role="option"]');
    if (rows.length === 0) { activeIndex = -1; return; }
    const max = rows.length - 1;
    activeIndex = Math.max(0, Math.min(max, i));
    paintActive();
    const el = rows[activeIndex] as HTMLElement;
    el?.scrollIntoView({ block: "nearest" });
  }

  function selectByName(name: string): void {
    const v = byNameLower.get(name.toLowerCase());
    if (!v) return;
    input.value = "";
    hideList();
    input.blur();
    map.flyTo({
      center: v.coord,
      zoom: Math.max(map.getZoom(), 13),
      duration: 700,
      essential: true,
    });
    showVerdict(v);
  }

  // ------------- input wiring ----------------------------------------

  input.addEventListener("input", () => {
    const q = input.value;
    if (q.trim().length === 0) {
      hideList();
      return;
    }
    renderList(q);
  });

  input.addEventListener("keydown", (e) => {
    // Only treat these keys as navigation when the list is open. With
    // an empty input Enter/ArrowDown should be no-ops, not trigger a
    // render of all suburbs.
    const listOpen = listbox.style.display !== "none";
    if (e.key === "ArrowDown") {
      if (!listOpen && input.value.trim().length >= 1) renderList(input.value);
      e.preventDefault();
      setActiveIndex(activeIndex + 1);
    } else if (e.key === "ArrowUp") {
      if (!listOpen) return;
      e.preventDefault();
      setActiveIndex(activeIndex - 1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const rows = listbox.querySelectorAll('li[role="option"]') as NodeListOf<HTMLLIElement>;
      if (activeIndex >= 0 && rows[activeIndex]?.dataset.name) {
        selectByName(rows[activeIndex].dataset.name!);
      } else {
        // Fall back to an exact-match lookup — typing the full name
        // and hitting Enter before arrowing down should still work.
        const v = byNameLower.get(input.value.trim().toLowerCase());
        if (v) selectByName(v.name);
      }
    } else if (e.key === "Escape") {
      if (listOpen) {
        e.preventDefault();
        hideList();
      } else if (input.value) {
        e.preventDefault();
        input.value = "";
      }
    }
  });

  // Clicking outside the wrap closes the dropdown. We listen on the
  // document instead of `blur` alone so a click on the map definitely
  // dismisses the panel (blur's timeout can race touch events).
  document.addEventListener("mousedown", (e) => {
    if (!wrap.contains(e.target as Node)) hideList();
  });

  wrap.appendChild(input);
  wrap.appendChild(listbox);
  document.body.appendChild(wrap);
}
